import { gql } from '@apollo/client';
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { InfiniteScrollBoundary } from 'components/common/InfiniteScrollBoundary';
import { IconCustomSort } from 'components/icons/components/custom/IconCustomSort';
import { IconCustomSortAsc } from 'components/icons/components/custom/IconCustomSortAsc';
import { IconCustomSortDesc } from 'components/icons/components/custom/IconCustomSortDesc';
import {
  CompetitorShareOfVoiceAggregatedDataItemFragmentBcShareOfVoiceTableFragment,
  CompetitorShareOfVoiceAggregatedDataSortByInputData,
  CompetitorShareOfVoiceAggregatedDataSortField,
  CompetitorShareOfVoiceFiltersInput,
  CreatorFragmentCreatorAvatarFragmentDoc,
  SortOrder,
  useGetCompetitorShareOfVoiceAggregatedDataForBrandCompetitorsShareOfVoiceTableQuery,
} from 'graphql/generated';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { theme } from 'styles/theme';
import { BrandCompetitorsShareOfVoiceTableRow } from './BrandCompetitorsShareOfVoiceTableRow';
import { BrandCompetitorsShareOfVoiceTableRowSkeleton } from './BrandCompetitorsShareOfVoiceTableRowSkeleton';

export const COMPETITOR_SHARE_OF_VOICE_AGGREGATED_DATA_ITEM_FRAGMENT_BC_SHARE_OF_VOICE_TABLE = gql`
  fragment CompetitorShareOfVoiceAggregatedDataItemFragmentBCShareOfVoiceTable on CompetitorShareOfVoiceAggregatedDataItem {
    id
    base {
      id
      brand {
        id
        name
        creators {
          id
          ...CreatorFragmentCreatorAvatar
        }
      }
      competitor {
        id
        name
      }
      previewPosts(filters: $previewPostsFilters) {
        id
        thumbnailUrl
        videoUrl
      }
    }
    aggregatedData {
      shareOfVoice
      mentionCount
      viewCount
      totalEngagement
      engagementRate
    }
  }
  ${CreatorFragmentCreatorAvatarFragmentDoc}
`;

// eslint-disable-next-line
gql`
  query GetCompetitorShareOfVoiceAggregatedDataForBrandCompetitorsShareOfVoiceTable(
    $filters: CompetitorShareOfVoiceFiltersInput!
    $sortBy: CompetitorShareOfVoiceAggregatedDataSortByInputData
    $skip: Int
    $take: Int
    $previewPostsFilters: CompetitorShareOfVoicePreviewPostsFiltersInput!
  ) {
    competitorShareOfVoiceAggregatedData(
      filters: $filters
      sortBy: $sortBy
      skip: $skip
      take: $take
    ) {
      data {
        id
        ...CompetitorShareOfVoiceAggregatedDataItemFragmentBCShareOfVoiceTable
      }
      pageInfo {
        hasNextPage
      }
    }
  }
  ${COMPETITOR_SHARE_OF_VOICE_AGGREGATED_DATA_ITEM_FRAGMENT_BC_SHARE_OF_VOICE_TABLE}
`;

export type BrandCompetitorsShareOfVoiceTableProps = {
  filters: Pick<
    CompetitorShareOfVoiceFiltersInput,
    'platforms' | 'dateRange' | 'q' | 'brandId'
  >;
  selectedItemIds: string[];
  setSelectedItemIds: Dispatch<SetStateAction<string[]>>;
  setItems: Dispatch<
    SetStateAction<
      CompetitorShareOfVoiceAggregatedDataItemFragmentBcShareOfVoiceTableFragment[]
    >
  >;
  itemIdToColorMap: Record<string, string>;
};

export const BrandCompetitorsShareOfVoiceTable = (
  props: BrandCompetitorsShareOfVoiceTableProps,
) => {
  const {
    filters,
    selectedItemIds,
    setSelectedItemIds,
    setItems,
    itemIdToColorMap,
  } = props;

  const [sortBy, setSortBy] =
    useState<CompetitorShareOfVoiceAggregatedDataSortByInputData>({
      field: CompetitorShareOfVoiceAggregatedDataSortField.MentionCount,
      order: SortOrder.Desc,
    });

  const {
    data,
    loading: isLoadingInitial,
    fetchMore,
  } = useGetCompetitorShareOfVoiceAggregatedDataForBrandCompetitorsShareOfVoiceTableQuery(
    {
      variables: {
        filters,
        sortBy,
        take: 99, // NOTE: Attempting to get all items at once. I need to observe the query performance.
        previewPostsFilters: {
          dateRange: {
            startDate: filters.dateRange.startDate,
            endDate: filters.dateRange.endDate,
          },
          platforms: filters.platforms,
        },
      },
      fetchPolicy: 'cache-and-network',
    },
  );
  const items = data?.competitorShareOfVoiceAggregatedData?.data || [];
  const hasNextPage =
    data?.competitorShareOfVoiceAggregatedData?.pageInfo.hasNextPage;
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isLoading = isLoadingInitial || isFetchingMore;

  const onFetchMore = () => {
    if (isLoading || !hasNextPage) {
      return;
    }

    setIsFetchingMore(true);

    fetchMore({
      variables: {
        filters,
        sortBy,
        skip: items.length,
        take: 99,
        previewPostsFilters: {
          dateRange: {
            startDate: filters.dateRange.startDate,
            endDate: filters.dateRange.endDate,
          },
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        return {
          competitorShareOfVoiceAggregatedData: {
            ...fetchMoreResult.competitorShareOfVoiceAggregatedData,
            data: [
              ...prev.competitorShareOfVoiceAggregatedData.data,
              ...fetchMoreResult.competitorShareOfVoiceAggregatedData.data,
            ],
          },
        };
      },
    }).finally(() => {
      setIsFetchingMore(false);
    });
  };

  // Select the first 5 items after we first fetch the data
  const [hasSetSelectedItemIds, setHasSetSelectedItemIds] = useState(false);
  useEffect(() => {
    if (
      items.length > 0 &&
      selectedItemIds.length === 0 &&
      !hasSetSelectedItemIds
    ) {
      setSelectedItemIds(items.slice(0, 5).map((item) => item.id));
      setHasSetSelectedItemIds(true);
    }

    setItems(items);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAnyItemSelected = items.length > 0 && selectedItemIds.length > 0;
  const areAllItemsSelected =
    items.length > 0 && selectedItemIds.length === items.length;

  const headCells = [
    {
      id: 'select',
      label: (
        <Checkbox
          size="small"
          indeterminate={isAnyItemSelected && !areAllItemsSelected}
          checked={areAllItemsSelected}
          sx={{
            p: 0,
          }}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedItemIds(items.map((item) => item.id));
            } else {
              setSelectedItemIds([]);
            }
          }}
        />
      ),
      minWidth: 40,
    },
    {
      id: 'competitor',
      label: 'Competitor',
      minWidth: 360,
    },
    {
      id: 'shareOfVoice',
      label: 'Share of voice',
      minWidth: 175,
    },
    {
      id: CompetitorShareOfVoiceAggregatedDataSortField.MentionCount,
      label: 'Number of mentions',
      canSort: true,
      minWidth: 175,
    },
    {
      id: CompetitorShareOfVoiceAggregatedDataSortField.ViewCount,
      label: 'View count',
      canSort: true,
      minWidth: 175,
    },
    {
      id: CompetitorShareOfVoiceAggregatedDataSortField.TotalEngagement,
      label: 'Total engagement',
      canSort: true,
      minWidth: 175,
    },
    {
      id: CompetitorShareOfVoiceAggregatedDataSortField.EngagementRate,
      label: 'Engagement rate',
      canSort: true,
      tooltip: 'Comments only',
      minWidth: 175,
    },
    {
      id: 'posts',
      label: 'Posts',
      minWidth: 175,
    },
  ];

  // We will only show the share of voice column if the item is selected
  const itemsWithUpdatedShareOfVoice = useMemo(() => {
    const selectedItems = items.filter((item) =>
      selectedItemIds.includes(item.id),
    );

    const totalShareOfVoice =
      selectedItems.reduce(
        (acc, curr) => acc + curr.aggregatedData.shareOfVoice,
        0,
      ) || 1;

    return items.map((item) => {
      if (selectedItemIds.includes(item.id)) {
        return {
          ...item,
          aggregatedData: {
            ...item.aggregatedData,
            shareOfVoice: item.aggregatedData.shareOfVoice / totalShareOfVoice,
          },
        };
      }

      return item;
    });
  }, [items, selectedItemIds]);

  return (
    <Box
      sx={{
        overflowX: 'auto',

        'th:last-child': {
          textAlign: 'right',
        },

        td: {
          border: 'none',
        },
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            {headCells.map((headCell) => (
              <TableCell
                key={headCell.id}
                sx={{
                  borderBottom: `1px solid ${theme.colors?.utility[400]}`,
                  minWidth: headCell.minWidth ?? 200,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="headline-xs"
                      color={theme.colors?.utility[700]}
                      whiteSpace="nowrap"
                    >
                      {headCell.label}
                    </Typography>
                  </Box>
                  {headCell.canSort && (
                    <IconButton
                      disableRipple
                      sx={{
                        p: 0,
                      }}
                      onClick={() => {
                        setSortBy((prev) => {
                          if (prev?.field === headCell.id) {
                            return {
                              ...prev,
                              order:
                                prev?.order === SortOrder.Asc
                                  ? SortOrder.Desc
                                  : SortOrder.Asc,
                            };
                          }
                          return {
                            field: headCell.id,
                            order: SortOrder.Asc,
                          };
                        });
                      }}
                    >
                      {sortBy?.field === headCell.id &&
                      sortBy?.order === SortOrder.Asc ? (
                        <IconCustomSortAsc
                          size={16}
                          color={theme.colors?.utility[800]}
                        />
                      ) : sortBy?.field === headCell.id &&
                        sortBy?.order === SortOrder.Desc ? (
                        <IconCustomSortDesc
                          size={16}
                          color={theme.colors?.utility[800]}
                        />
                      ) : (
                        <IconCustomSort
                          size={16}
                          color={theme.colors?.utility[600]}
                        />
                      )}
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {itemsWithUpdatedShareOfVoice.map((item) => (
            <BrandCompetitorsShareOfVoiceTableRow
              key={item.id}
              brandId={filters.brandId}
              item={item}
              isSelected={selectedItemIds.includes(item.id)}
              color={itemIdToColorMap[item.id]}
              onSelect={() =>
                setSelectedItemIds((prev) =>
                  prev.includes(item.id)
                    ? prev.filter((id) => id !== item.id)
                    : [...prev, item.id],
                )
              }
            />
          ))}
          {isLoading &&
            new Array(5)
              .fill(null)
              .map((_, index) => (
                <BrandCompetitorsShareOfVoiceTableRowSkeleton key={index} />
              ))}
          <InfiniteScrollBoundary
            onVisible={onFetchMore}
            disabled={isLoading || !hasNextPage}
          />
        </TableBody>
      </Table>
      {!isLoading && items.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 10,
          }}
        >
          <Typography variant="subhead-xl" color={theme.colors?.utility[700]}>
            No data found
          </Typography>
        </Box>
      )}
    </Box>
  );
};
